import { Empty, Input, Modal, Spin, Tag, type InputRef } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiChevronRight, FiSearch } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { getDayjsLocaleByLanguage } from '@app/i18n/helpers';
import { LIST_DATE_FORMAT, LIST_TIME_FORMAT } from '@app/utils/dates/dateFormats';
import { RequestStatusEnum } from '@api/READ_ONLY/service_request_api/Api';
import { VisitStatusEnum } from '@api/READ_ONLY/visits_api/Api';
import { visitStatusColor, visitStatusLabelKey } from '@feat-visits/visitStatus';
import type { TranslationKeys } from '@app/i18n/translationKeys';
import { entityDef, type EntityDef } from './entityRegistry';
import { fieldLabelKey } from './fields';
import { addRecent, readRecent } from './prefs';
import { snippet } from './snippet';
import type { SearchGroup, SearchItem } from './types';
import { useGlobalSearch } from './useGlobalSearch';
import { inDisplayZone } from '@app/utils/dates/displayTimezone';

type Row =
  | { kind: 'header'; type: string }
  | { kind: 'item'; type: string; item: SearchItem }
  | { kind: 'more'; type: string };

interface Props {
  open: boolean;
  onClose: () => void;
  /** Entity types enabled for this user (registry order). */
  types: string[];
  /** Recent results are kept per user; a non-positive id remembers nothing. */
  userId: number;
}

// A category with more results than this opens collapsed; the user's toggle wins until the query changes.
const AUTO_COLLAPSE_OVER = 5;
const DATED = new Set(['visit', 'shift', 'note', 'service_request']);
const VISIT_STATUSES = new Set<string>(Object.values(VisitStatusEnum));
const REQUEST_STATUSES = new Set<string>(Object.values(RequestStatusEnum));

const optionId = (index: number) => `global-search-option-${index}`;
const MAX_QUERY_LENGTH = 200;

export function GlobalSearchPalette({ open, onClose, types, userId }: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const { groups, loading, error } = useGlobalSearch(query, types);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<InputRef>(null);
  const trimmed = query.trim();

  // Registry order; unknown types from a newer backend are skipped, never rendered with a fallback.
  const known = useMemo(() => {
    const byType = new Map(groups.map((g) => [g.entityType, g]));
    groups
      .filter((g) => !entityDef(g.entityType))
      .forEach((g) => console.warn('global search: unknown entity type', g.entityType));
    return types.map((type) => byType.get(type)).filter((g): g is SearchGroup => Boolean(g && g.items.length));
  }, [groups, types]);

  const recent = useMemo(
    () => (open && !trimmed ? readRecent(userId).filter((r) => types.includes(r.entityType)) : []),
    [open, trimmed, types, userId]
  );

  const isCollapsed = useMemo(() => {
    const totals = new Map(known.map((g) => [g.entityType, g.total]));
    return (type: string) => overrides[type] ?? (totals.get(type) ?? 0) > AUTO_COLLAPSE_OVER;
  }, [known, overrides]);

  const rows: Row[] = useMemo(() => {
    if (error) return [];
    if (!trimmed) return recent.map((r) => ({ kind: 'item' as const, type: r.entityType, item: r.item }));
    return known.flatMap((g) => {
      const header: Row = { kind: 'header', type: g.entityType };
      if (isCollapsed(g.entityType)) return [header];
      const items: Row[] = g.items.map((item) => ({ kind: 'item', type: g.entityType, item }));
      const more: Row[] = g.hasMore && entityDef(g.entityType)?.listPath ? [{ kind: 'more', type: g.entityType }] : [];
      return [header, ...items, ...more];
    });
  }, [error, trimmed, recent, known, isCollapsed]);

  // Start on the first result, not a header; reset on new results only, not on a collapse.
  const firstItem = useMemo(() => rows.findIndex((r) => r.kind === 'item'), [rows]);
  const [results, setResults] = useState({ trimmed, known, recent });
  if (results.trimmed !== trimmed || results.known !== known || results.recent !== recent) {
    if (results.trimmed !== trimmed) setOverrides({});
    setResults({ trimmed, known, recent });
    setActive(Math.max(0, firstItem));
  } else if (active > 0 && active >= rows.length) {
    setActive(Math.max(0, rows.length - 1));
  }

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const toggle = (type: string, collapse?: boolean) => {
    setOverrides((prev) => ({ ...prev, [type]: collapse ?? !isCollapsed(type) }));
  };

  const go = (to: string) => {
    onClose();
    navigate(to);
  };

  const activate = (row: Row | undefined) => {
    if (!row) return;
    const def = entityDef(row.type);
    if (!def) return;
    if (row.kind === 'header') return toggle(row.type);
    if (row.kind === 'more') return go(`${def.listPath}?search=${encodeURIComponent(trimmed)}`);
    addRecent(userId, row.type, row.item);
    go(def.route(row.item));
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.max(0, Math.min(rows.length - 1, i + 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      activate(rows[active]);
    } else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && rows[active]?.kind === 'header') {
      // Only on a header: anywhere else the arrows move the caret in the input.
      e.preventDefault();
      toggle(rows[active].type, e.key === 'ArrowLeft');
    }
  };

  const formatWhen = (item: SearchItem, type: string) => {
    if (!item.sortAt || !DATED.has(type)) return null;
    const format =
      type === 'note' || type === 'service_request' ? LIST_DATE_FORMAT : `${LIST_DATE_FORMAT} ${LIST_TIME_FORMAT}`;
    return inDisplayZone(item.sortAt).locale(getDayjsLocaleByLanguage(i18n.language)).format(format);
  };

  const renderItem = (row: Extract<Row, { kind: 'item' }>, index: number, def: EntityDef) => {
    const { item } = row;
    const Icon = def.icon;
    const labelKey = item.matched ? fieldLabelKey(item.matched.label) : undefined;
    const line = item.matched
      ? `${labelKey ? `${t(labelKey)}: ` : ''}${snippet(item.matched.value, trimmed)}`
      : item.subtitle;
    const when = formatWhen(item, row.type);
    return (
      <div
        key={`${row.type}-${item.id}`}
        id={optionId(index)}
        role="option"
        aria-selected={index === active}
        onMouseEnter={() => setActive(index)}
        onClick={() => activate(row)}
        className={`flex cursor-pointer items-start gap-3 rounded-md px-3 py-2 transition-colors ${index === active ? 'bg-blue-50' : ''} ${item.inactive ? 'opacity-60' : ''}`}
      >
        <Icon className="mt-1 shrink-0 text-gray-500" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium">{item.title}</span>
            {row.type === 'payroll_run' && Number(item.meta?.revision) > 1 && (
              <span className="shrink-0 text-xs text-gray-500">
                {t('global-search-revision', { n: Number(item.meta.revision) })}
              </span>
            )}
            <StatusTag type={row.type} status={item.status} />
          </div>
          {(line || when) && (
            <div className="truncate text-xs text-gray-500">{[when, line].filter(Boolean).join(' · ')}</div>
          )}
        </div>
        {/* Faint on every row so rows read as openable; the selected one also shows the Enter key. */}
        <span
          data-testid="gs-row-affordance"
          aria-hidden
          className={`flex shrink-0 items-center gap-1 self-center transition-colors ${index === active ? 'text-blue-500' : 'text-gray-300'}`}
        >
          {index === active && <kbd className="rounded border border-blue-200 px-1 text-[10px] leading-4">↵</kbd>}
          <FiChevronRight />
        </span>
      </div>
    );
  };

  const body = () => {
    if (error)
      return (
        <div role="alert" className="px-3 py-6 text-center text-red-600">
          {t('global-search-error')}
        </div>
      );
    if (!trimmed) {
      if (!rows.length) return null;
      return (
        <div>
          <div className="px-3 pb-1 pt-2 text-xs uppercase text-gray-400">{t('global-search-recent')}</div>
          {rows.map((row, i) => row.kind === 'item' && entityDef(row.type) && renderItem(row, i, entityDef(row.type)!))}
        </div>
      );
    }
    if (!loading && !known.length) {
      return (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('global-search-no-results', { query: trimmed })} />
      );
    }
    return known.map((g) => {
      const def = entityDef(g.entityType)!;
      const Icon = def.icon;
      const groupCollapsed = isCollapsed(g.entityType);
      const renderRow = (row: Row, i: number) => {
        if (row.kind === 'header') {
          return (
            <div
              key="header"
              data-testid="gs-group-header"
              role="option"
              id={optionId(i)}
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => {
                setActive(i);
                toggle(g.entityType);
              }}
              className={`flex cursor-pointer items-center gap-2 px-3 pb-1 pt-2 text-xs font-semibold uppercase text-gray-500 ${i === active ? 'bg-gray-50' : ''}`}
            >
              {groupCollapsed ? <FiChevronRight aria-hidden /> : <FiChevronDown aria-hidden />}
              <Icon aria-hidden />
              <span>{t(def.labelKey)}</span>
              <span className="font-normal">({g.total > 20 ? t('global-search-more') : g.total})</span>
            </div>
          );
        }
        if (row.kind === 'more') {
          return (
            <div
              key="more"
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => activate(row)}
              className={`cursor-pointer px-3 py-1 text-xs !text-blue-600 ${i === active ? 'bg-blue-50' : ''}`}
            >
              <span>{t('global-search-see-all')}</span> <span aria-hidden>→</span>
            </div>
          );
        }
        return renderItem(row, i, def);
      };
      const own = rows.map((row, i) => [row, i] as const).filter(([row]) => row.type === g.entityType);
      const children = own.filter(([row]) => row.kind !== 'header');
      return (
        <div key={g.entityType} data-testid="gs-group" className="mb-1">
          {own.filter(([row]) => row.kind === 'header').map(([row, i]) => renderRow(row, i))}
          {children.length > 0 && (
            // Indented under the header with a guide line, so each row reads as belonging to its category.
            <div data-testid="gs-group-children" className="ml-5 border-l border-gray-200 pl-2">
              {children.map(([row, i]) => renderRow(row, i))}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={640}
      destroyOnHidden
      // The modal focuses its own wrapper once the open animation ends, which beats autoFocus.
      afterOpenChange={(visible) => visible && inputRef.current?.focus()}
      styles={{ body: { padding: 0 } }}
      style={{ top: 80 }}
    >
      <div className="border-b p-3">
        <Input
          ref={inputRef}
          autoFocus
          role="combobox"
          aria-expanded
          aria-controls="global-search-results"
          aria-activedescendant={rows[active] ? optionId(active) : undefined}
          size="large"
          variant="borderless"
          prefix={<FiSearch aria-hidden />}
          suffix={loading ? <Spin size="small" /> : null}
          placeholder={t('global-search-placeholder')}
          maxLength={MAX_QUERY_LENGTH}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
        />
      </div>
      <div id="global-search-results" ref={listRef} role="listbox" className="max-h-[60vh] overflow-y-auto p-2">
        {body()}
      </div>
      <div className="border-t px-3 py-2 text-xs text-gray-400">{t('global-search-hint')}</div>
    </Modal>
  );
}

// Greek "inactive" agrees in gender; reuse the masculine labels the app already has.
const INACTIVE_KEY: Partial<Record<string, TranslationKeys>> = {
  client: 'clients-status-inactive',
  doctor: 'doctors-status-inactive',
  caregiver: 'clients-status-inactive',
  service: 'services-tag-inactive',
};

function StatusTag({ type, status }: { type: string; status?: string | null }) {
  const { t } = useTranslation();
  if (!status) return null;
  if (status === 'DECEASED') return <Tag>{t('global-search-deceased')}</Tag>;
  if (status === 'INACTIVE') return <Tag>{t(INACTIVE_KEY[type] ?? 'global-search-inactive')}</Tag>;
  if (status === 'DISABLED') return <Tag>{t('global-search-disabled')}</Tag>;
  // Only statuses that already have a label in the app; anything else gets no tag.
  if (type === 'visit' && VISIT_STATUSES.has(status)) {
    return <Tag color={visitStatusColor(status)}>{t(visitStatusLabelKey(status))}</Tag>;
  }
  if (type === 'service_request' && REQUEST_STATUSES.has(status)) {
    return <Tag>{t(`service-requests-status-${status.toLowerCase()}`)}</Tag>;
  }
  return null;
}
